// Shared, presentation-only model for the live-meeting summary, rendered by both
// the Chief native app and the bluesky web app. Each consumer maps its own data
// (Chief's wire store, the web's session payload) onto these shapes.

/**
 * What a live-summary point is. The model classifies its own points as context,
 * a decision or a to-do; a note is a bullet a participant wrote themselves.
 *
 * The one vocabulary, because every surface that sorts, counts, filters or
 * routes a point answers to it: Chief's notes panel and its chronological
 * timeline, the web session page, and the store each app maps its wire payload
 * onto. A kind added here, or one retired, is a change in this list rather than
 * the same change made in every place that spells the four out.
 */
export const LIVE_SUMMARY_KINDS = ["context", "decision", "todo", "note"] as const;

export type LiveSummaryKind = (typeof LIVE_SUMMARY_KINDS)[number];

/** A live-session tag. Shares the project's label vocabulary but a separate
 *  audience, so it never appears among the web app's labels. */
export type LiveSummaryTag = {
  id: string;
  name: string;
  color: string;
  pinned?: boolean;
  /** Whether the tag applies itself. A tag whose switch is off is a plain tag. */
  smart?: boolean;
  /** The sentence a Smart Tag matches on, shown wherever the reader needs to
   *  know why something carries it. Empty on a plain tag, and on one of Chief's
   *  defaults, whose matching is the summary's own classification. */
  instruction?: string;
  /** Set on the tags every project starts with, naming the classification
   *  each is backed by. Their matching is the summary's own, not an instruction,
   *  so nothing evaluates one for them. Typed loosely because it crosses the
   *  wire as a string, and a kind this build doesn't know simply matches
   *  nothing. */
  defaultKind?: string;
  /** Set on a tag the project soft-deleted. The definition is still handed over
   *  so content carrying the id goes on rendering its name and colour, but the
   *  tag is no longer derived onto anything and no longer offered. */
  deleted?: boolean;
};

export type LiveSummaryItemData = {
  id: string;
  kind: LiveSummaryKind;
  topic?: string;
  text: string;
  owner?: string;
  importance?: number;
  // firstSeenSec is the audio-time the item was first observed, used to render a
  // note's timecode.
  firstSeenSec?: number;
  // quote is the transcript text a note was written against; empty on model items.
  quote?: string;
  // tagIds are the user's tags on this item. Never written by the model.
  tagIds?: string[];
  // autoTagIds are the tags a Smart Tag put on this item. Held apart from tagIds
  // so a pass can replace the whole set without touching what a person applied.
  autoTagIds?: string[];
  // removedAutoTagIds are auto-applied tags somebody took off by hand, kept so a
  // later pass re-emitting the tag doesn't put it back.
  removedAutoTagIds?: string[];
  // parentId is set when this item is a sub-point of another; empty/undefined for
  // a top-level main point. One level only.
  parentId?: string;
  // active = shown in the summary; false = sits in the topic's collapsed "set
  // aside" sub-section (the user dismissed or set it aside).
  active: boolean;
  // The consumer's persisted item state, passed through from its own store.
  // The view only reads `state === "done"` to strike a completed to-do through;
  // the other values ("open"/"approved") render identically here.
  state?: string;
};

// "all", one kind, or one tag — the filter pills above the summary. A tag only
// ever gets a pill once something in the session carries it.
export type LiveSummaryFilter = "all" | LiveSummaryKind | `tag:${string}`;

export const tagFilter = (tagId: string): LiveSummaryFilter => `tag:${tagId}`;

/** The tag id a filter selects, or null when it selects "all" or a kind. */
export const filterTagId = (filter: LiveSummaryFilter): string | null =>
  filter.startsWith("tag:") ? filter.slice("tag:".length) : null;

// A main (top-level) point with its active sub-points nested beneath it.
export type LiveSummaryMainData = {
  item: LiveSummaryItemData;
  children: LiveSummaryItemData[];
};

// A topic split into its active mains (each carrying its sub-points) and the flat
// list of set-aside items (mains and sub-points the user removed).
export type LiveSummaryTopicData = {
  title: string;
  items: LiveSummaryMainData[];
  dropped: LiveSummaryItemData[];
};
